<script lang="ts">
import {
  defineComponent,
  useSlots,
  PropType,
  ref,
  computed,
  watch,
  onMounted,
} from 'vue'
import { useConditionalScreenLock } from '@/composables/useScreenLock'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'ModalCore',

  props: {
    /**
     * モーダルの aria-label 属性に付与されるべきテキスト
     * @description header スロットがある場合はそちらの内包テキストを優先する（そちらがなかった場合のフォールバックとして本 props の値が参照される）
     */
    title: {
      type: String,
      default: '',
    },

    /**
     * モーダルヘッダ文言
     */
    headerMessage: {
      type: String,
      default: '',
    },

    /**
     * モーダルヘッダ文言のアラインメント
     */
    headerMessageAlignment: {
      type: String as PropType<'left' | 'center' | 'right'>,
      default: 'center',
    },

    /**
     * モーダルの表示切り替え制御フラグ
     */
    isShow: {
      type: Boolean,
      required: true,
    },

    /**
     * モーダルを表示した際に背景を暗くするかどうか
     */
    isBlackOut: {
      type: Boolean,
      default: false,
    },

    /**
     * モーダルを表示した際に背景をぼかすかどうか
     */
    isBlur: {
      type: Boolean,
      default: false,
    },

    /**
     * スロットコンテンツ外をクリックした際にモーダルを閉じる
     */
    enableBackdrop: {
      type: Boolean,
      default: true,
    },

    /**
     * 閉じるボタンが使えるかどうか
     */
    enableClose: {
      type: Boolean,
      default: true,
    },

    /**
     * モーダルのサイズ
     */
    size: {
      type: String as PropType<'mini' | 'small' | 'normal' | 'large' | 'xLarge' | 'xxLarge' | 'huge'>,
      default: 'normal',
    },

    /**
     * モーダルの状態修飾子用 CSS クラス
     */
    stateStyleClass: {
      type: String as PropType<'is-lite' | 'is-border'>,
      default: '',
    },

    /**
     * その他の追加 CSS クラス（汎用 Utility CSS クラス等）
     */
    additionalStyleClass: {
      type: Array as PropType<string[]>,
      default: () => [],
    },

    /**
     * @description この props に DOM 要素を渡すことで、
     *              渡した瞬間にそちらの DOM までモーダル内スクロールします
     */
    scrollTo: {
      type: Object as PropType<HTMLElement>,
      default: () => ({}),
    },

    /**
     * モーダルの aria-labelledby 属性に付与する要素の id
     */
    ariaTitleId: {
      type: String,
      default: 'modal-title',
    }
  },

  emits: ['update:isShow', 'complete:scroll', 'close'],

  setup(props, { emit }) {
    const slots = useSlots()
    const body: Ref<HTMLElement | null> = ref(null)
    const frame: Ref<HTMLElement | null> = ref(null)
    const description: Ref<HTMLElement | null> = ref(null)

    const background: Ref<HTMLElement[]> = ref([])
    const heading: Ref<HTMLElement | null> = ref(null)

    const hasHeader = computed<boolean>(() => {
      const v = !!slots.header && slots.header().some(vnode => vnode.children || vnode.props)
      return v || !!props.headerMessage
    })

    const hasDescription = computed<boolean>(() => {
      const v = !!slots.description && slots.description().some(vnode => vnode.children || vnode.props)
      return v
    })

    const hasAction = computed<boolean>(() => {
      const v = !!slots.action && slots.action().some(vnode => vnode.children || vnode.props)
      return v
    })

    const hasFooter = computed<boolean>(() => {
      const v = !!slots.footer && slots.footer().some(vnode => vnode.children || vnode.props)
      return v
    })

    /**
     * モーダルヘッダ文言のアラインメント指定に対する CSS クラス
     */
    const headerMessageAlignmentClass = computed<string>(() => {
      // left の場合は無指定（h2 のデフォルトが左寄せのため）
      if (props.headerMessageAlignment === 'left') return ''

      return props.headerMessageAlignment === 'right'
        ? 'u-text__right'
        : 'u-text__center'
    })

    // 表示中はロックを持つ。初期表示で isShow が true の場合（プロフィール入力モーダル等）もロックされる。
    // ここ以外でロックを操作しないこと
    useConditionalScreenLock(
      () => props.isShow,
      () => ({
        hasBlackOut: props.isBlackOut,
        hasBlur: props.isBlur,
      }),
    )

    watch(() => props.isShow, (newValue) => {
      if (!newValue) {
        // モーダル表示時にモーダル内のスクロール位置リセット
        // フェードアウトの CSS transition 最中にスクロール位置がリセットしてしまい、
        // ガタつきが生じるので、setTimeout で transition 時間分の delay を置く
        const CSS_TRANSITION_DURATION = 0.3
        setTimeout(() => {
          if (description.value) {
            description.value.scrollTop = 0
          }
        }, CSS_TRANSITION_DURATION * 1000)
      }
    })

    /**
     * props の scrollTo に HTML Element を渡した瞬間に、
     * そちらの座標までモーダル内スクロールする
     */
    watch(() => props.scrollTo, (oldValue, newValue) => {
      if (oldValue === newValue) return
      if (!props.scrollTo || !description.value) return

      const fromY = description.value.getBoundingClientRect().y
      const toY = props.scrollTo.getBoundingClientRect().y

      description.value.scrollBy({
        top: toY - fromY,
        behavior: 'smooth',
      })

      emit('complete:scroll')
    })

    onMounted(() => {
      // スロットコンテンツ外ターゲットエリアを取得しておく。
      if (body.value !== null) background.value.push(body.value)
      if (frame.value !== null) background.value.push(frame.value)
    })

    /**
     * モーダルを閉じる
     * @description ロックはここで解除しない。親が isShow を false にした時点で useConditionalScreenLock が手放す（ここで解除すると二重管理になる）
     */
    const hideModal = () => {
      emit('update:isShow', false)
      emit('close')
    }

    /**
     * スロットコンテンツ本体部分以外の場所がクリックされたらスロットコンテンツを閉じる
     *
     * @param {Event} event
     */
    const backDrop = (event: Event) => {
      if (props.enableBackdrop) {
        if (background.value.includes((event.target as HTMLElement))) {
          hideModal()
        }
      }
    }

    /**
     * モーダルのレイヤーがクリックされた際に、背景レイヤーにイベントが伝播することがあるため防止する
     * @param e Window ポインターイベント
     */
    const onClickModalLayer = (e: Event) => {
      e.stopPropagation()
    }

    return {
      // data
      body,
      frame,
      description,
      heading,

      // computed
      hasHeader,
      hasDescription,
      hasAction,
      hasFooter,
      headerMessageAlignmentClass,

      // methods
      hideModal,
      backDrop,
      onClickModalLayer,
    }
  },
})
</script>

<template>
  <transition name="is-fade">
    <section
      v-show="isShow"
      ref="frame"
      class="f-section__overlay"
      @click.stop="backDrop"
      @keydown.enter="backDrop"
      @keydown.space.prevent="backDrop">
      <div
        ref="body"
        class="f-section__overlay--body">
        <transition name="is-popin">
          <div
            v-show="isShow"
            :class="[
              'l-unit__modal',
              `is-${size}`,
              stateStyleClass,
              additionalStyleClass,
            ]"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="hasHeader ? ariaTitleId : undefined"
            :aria-label="hasHeader ? undefined : title || 'ダイアログ'"
            @click="onClickModalLayer($event)">
            <div
              v-if="hasHeader"
              class="l-unit__modal--header">
              <div
                :id="ariaTitleId"
                ref="heading"
                class="div">
                <slot name="header" />
              </div>

              <div
                v-if="headerMessage"
                class="o-heading__modal">
                <p
                  :class="[
                    'o-heading__modal--item',
                    headerMessageAlignmentClass,
                  ]">
                  {{ headerMessage }}
                </p>
              <!-- /.o-heading__modal --></div>
            <!-- /.l-unit__modal header --></div>

            <div
              v-if="hasDescription"
              ref="description"
              class="l-unit__modal--description">
              <slot name="description" />
            <!-- /.l-unit__modal description --></div>

            <div
              v-if="hasAction"
              class="l-unit__modal--action">
              <slot name="action" />
            <!-- /.l-unit__modal action --></div>

            <div
              v-if="enableClose"
              class="l-unit__modal--action is-close">
              <div
                class="o-action__close u-action__tooltip"
                data-tooltip="閉じる">
                <button
                  class="o-action__close--item"
                  @click.prevent="hideModal">
                  閉じる
                </button>
              <!-- /.o-action__close --></div>
            <!-- /.l-unit__modal action --></div>

            <div
              v-if="hasFooter"
              class="l-unit__modal--footer">
              <slot name="footer" />
            <!-- /.l-unit__modal footer --></div>
          <!-- /.l-unit__modal --></div>
        </transition>
      <!-- /.f-section__overlay body --></div>
    <!-- /.f-section__overlay --></section>
  </transition>
</template>
